import { Fragment } from "react";
import { InfiniteListBase } from "ra-core";
import type { Identifier } from "ra-core";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

import { Note } from "./Note";
import { NoteCreate } from "./NoteCreate";
import { BCInteractionEntry } from "./BCInteractionEntry";
import { useContactTimeline } from "./useContactTimeline";

/**
 * NoteCreate uses useListContext() internally, so it must be rendered inside
 * an InfiniteListBase that provides the ListContext. We wrap here so that
 * useContactTimeline (which merges contact_notes + bc_interaction_log) drives
 * the display, while InfiniteListBase only provides the context NoteCreate needs.
 */
export const ContactTimelineIterator = ({
  contactId,
  showStatus,
}: {
  contactId: Identifier;
  showStatus?: boolean;
}) => {
  return (
    <InfiniteListBase
      resource="contact_notes"
      filter={{ contact_id: contactId }}
      sort={{ field: "date", order: "DESC" }}
      perPage={25}
      disableSyncWithLocation
      storeKey={false}
    >
      <TimelineContent contactId={contactId} showStatus={showStatus} />
    </InfiniteListBase>
  );
};

const TimelineContent = ({
  contactId,
  showStatus,
}: {
  contactId: Identifier;
  showStatus?: boolean;
}) => {
  const { data, isPending } = useContactTimeline(contactId);

  if (isPending) {
    return (
      <div className="mt-4 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-12 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <NoteCreate reference="contacts" showStatus={showStatus} />
      {data.length > 0 && (
        <div className="mt-4 space-y-4">
          {data.map((item, index) => (
            <Fragment
              key={
                item.type === "note"
                  ? `note-${item.data.id}`
                  : `bc-${item.data.id}`
              }
            >
              {item.type === "note" ? (
                <Note
                  note={item.data}
                  isLast={index === data.length - 1}
                  showStatus={showStatus}
                />
              ) : (
                <BCInteractionEntry interaction={item.data} />
              )}
              {index < data.length - 1 && <Separator />}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
